import type { PdfEngine } from "@pdfking/pdfengine"
import { CreateRenderFunction } from "@pdfking/reactivity/preact"
import { Void } from "@pdfking/tfunc"

import { NativeDrop } from "#compatibility"
import type { DroppedFiles } from "#public"

import { Translate } from "../i18n/useTranslation"

/**
 * The window's drop target, as a component: mount it beside the viewer and
 * files dropped anywhere on the window reach that host's `on_files`. This is
 * what {@link UploadCard} means when it says "drag and drop" — the card carries
 * no drop handling of its own because a drop is caught once, here.
 *
 * The viewer does not mount one itself: what a drop *means* is the host's (its
 * route is where the files land), so the host mounts this and answers that.
 * Everything about *how* they arrive lives behind the build's own `NativeDrop`
 * — ingested bytes on the web, `asset:` mirrors on the desktop — which reports
 * both the hover state and the resolved files. So this owns nothing but the
 * subscription and the full-window highlight.
 *
 * The highlight is `fixed inset-0` rather than anything the host lays out: a
 * drop is a window gesture, accepted over the whole surface including the
 * chrome no view paints. Embedded, that resolves against the host's own
 * containing block instead — the filecheck modal transforms its body for
 * exactly this (see the app's pdfview.css), so the overlay covers the modal
 * rather than the page behind it.
 */
export const DropArea = CreateRenderFunction({
  data: {
    dragging: false,
    unsubscribe: undefined as (() => void) | undefined,
    // Set once unmounted, so a NativeDrop subscription that resolves after
    // this point tears itself down instead of leaking.
    unmounted_early: false,
  },
  mounted() {
    void NativeDrop(this.engine, {
      on_dragging: (dragging: boolean) => (this.dragging = dragging),
      on_files: (files: DroppedFiles) => this.on_files(files),
    }).then((off: () => void) => (this.unmounted_early ? off() : (this.unsubscribe = off)))
  },
  props: {
    // The engine the dropped bytes are ingested into — the same one the host
    // built its viewer with, so a drop and a picked file land identically.
    engine: undefined as unknown as PdfEngine,
    on_files: Void as (files: DroppedFiles) => void,
  },
  render() {
    if (!this.dragging) return undefined
    // `Translate` rather than `useTranslationTyped`: the hook calls `useState`
    // internally, and this render bails out above — a conditional hook would
    // misalign the hook list between dragging and idle renders.
    return (
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center border-4 border-dashed border-ring bg-accent/50 text-sm font-medium text-accent-foreground">
        <Translate library="actions" name="DROP_FILES" />
      </div>
    )
  },
  unmounted() {
    this.unmounted_early = true
    this.unsubscribe?.()
  },
})
